<template>
  <div
    class="column q-my-md tw-bg-gray-100 q-pa-md tw-rounded-md"
    v-show="
      isVisible &&
      !shouldBeHidden &&
      displayRaiReasonCodes &&
      displayAdQuantity &&
      displayQuantityReasonCode
    "
  >
    <q-skeleton v-if="!fieldLoaded"></q-skeleton>
    <template v-else>
      <ul v-if="errors && errors.length">
        <li v-for="error in errors" :key="error">
          <strong class="text-red">{{ error }}</strong>
        </li>
      </ul>
      <template v-if="shouldDisplayName">
        <span class="text-h6">{{ fieldName }}</span>
      </template>
      <template v-if="fieldType === 'FIELD_YES_NO'">
        <label class="column">
          <span class="text-h6">{{ fieldName }}</span>
          <q-radio v-model="(value as any)" :val="(true as any)" label="Yes" />
          <q-radio v-model="(value as any)" :val="(false as any)" label="No" />
        </label>
      </template>
      <template v-if="fieldType === 'FIELD_INTEGER'">
        <q-input outlined v-model="(value as any)" type="number" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_TEXT'">
        <q-input outlined v-model="(value as any)" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_FLOAT1'">
        <q-input outlined v-model="(value as any)" type="number" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_FLOAT2'">
        <q-input outlined v-model="(value as any)" type="number" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_MONEY'">
        <q-input outlined v-model="(value as any)" :label="fieldName">
          <template #prepend>
            <q-icon name="las la-dollar-sign" />
          </template>
        </q-input>
      </template>
      <template v-if="fieldType === 'FIELD_SELECT_RADIO'">
        <label>
          <span class="text-h6">{{ fieldName }}</span>
          <q-option-group
            v-model="value"
            :options="filteredOptions"
            type="radio"
            :label="fieldName"
          />
        </label>
      </template>
      <template
        v-if="
          fieldType === 'FIELD_SELECT' ||
          fieldType === 'FIELD_IMAGE_REASON_CODE' ||
          fieldType === 'FIELD_IMAGE_REASON_CODE_CUSTOM'
        "
      >
        <label>
          <span class="text-h6">{{ fieldName }}</span>
          <q-option-group
            v-if="filteredOptions.length < 7"
            v-model="value"
            :options="filteredOptions"
            type="radio"
            :label="fieldName"
          />
          <q-select
            v-else
            v-model="value"
            option-label="label"
            option-value="value"
            :options="filteredOptions"
            emit-value
            map-options
          >
          </q-select>
        </label>
      </template>
      <template v-if="fieldType === 'FIELD_MULTISELECT'">
        <label>
          <span class="text-h6">{{ fieldName }}</span>
          <q-option-group
            v-if="filteredOptions.length < 7"
            v-model="value"
            :option-value="(opt: any) => opt.value"
            :options="filteredOptions"
            type="checkbox"
            :label="fieldName"
          />
          <q-select
            v-else
            v-model="value"
            option-label="label"
            option-value="value"
            :options="filteredOptions"
            emit-value
            map-options
            multiple
          >
          </q-select>
        </label>
      </template>
      <template v-if="fieldType === 'FIELD_RAI_CODE'">
        <label>
          <span class="text-h6">{{ fieldName }}</span>
          <q-option-group
            v-if="filteredOptions.length < 7"
            v-model="value"
            :options="filteredOptions"
            type="radio"
          />
          <q-select
            v-else
            v-model="value"
            option-label="label"
            option-value="value"
            :options="filteredOptions"
            emit-value
            map-options
          >
          </q-select>
        </label>
      </template>
      <template v-if="fieldType === 'FIELD_RAI_REASON_CODE'">
        <label>
          <span class="text-h6">{{ fieldName }}</span>
          <q-option-group
            v-if="raiReasonCodes.length < 7"
            v-model="value"
            :options="raiReasonCodes"
            type="radio"
            :label="fieldName"
          >
            <template #label="opt">
              <span>{{ (opt.label as any)[recapLanguage] || (opt.label as any)['en_US'] }}</span>
            </template>
          </q-option-group>
          <q-select
            v-else
            v-model="value"
            :option-label="getOptionText"
            :option-value="getRaiReasonCodeValue"
            :options="raiReasonCodes"
            emit-value
            map-options
          >
          </q-select>
        </label>
      </template>
      <template v-if="fieldType === 'FIELD_DATE'">
        <q-date minimal v-model="value" />
      </template>
      <template v-if="fieldType === 'FIELD_SUBSECTION'">
        <h3 class="text-h3">{{ fieldName }}</h3>
        <hr />
      </template>
      <template v-if="fieldType === 'FIELD_TEXTAREA'">
        <q-input v-model="(value as any)" type="textarea" />
      </template>
      <template v-if="fieldType === 'FIELD_TOTAL'">
        <q-input outlined v-model="(value as any)" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_TIME'">
        <q-time v-model="(value as any)" />
      </template>
      <template v-if="fieldType === 'FIELD_IMAGE_UPLOAD'">
        <PhotoUploader
          :recap-field-instance-id="getFieldId()"
          :recap-field-instance-repeating-index="props.repeatingIndex"
          :file-count="fileCount"
          :multiple="false"
          @all-files-removed="allFilesRemoved"
          @file-added="fileAdded"
          ref="singleImageUploader"
        ></PhotoUploader>
      </template>
      <template v-if="fieldType === 'FIELD_SIGNATURE_CAPTURE'">
        <canvas class="tw-border-2" ref="signaturePad"></canvas>
        <div class="tw-flex tw-align-items-end">
          <div class="tw-flex-grow"></div>
          <q-btn class="tw-w-48 q-my-sm" color="red" @click="resetCanvas">Reset Signature</q-btn>
        </div>
      </template>
      <template v-if="fieldType === 'FIELD_DATETIME'">
        <div class="row">
          <q-date v-model="value" mask="YYYY-MM-DD HH:mm" class="q-mx-sm" />
          <q-time v-model="(value as any)" mask="YYYY-MM-DD HH:mm" class="q-mx-sm" />
        </div>
      </template>
      <template v-if="fieldType === 'FIELD_DURATION'">
        <div class="row">
          <q-input outlined v-model="(value as any)" label="Hours" class="q-mx-sm" />
          <q-input outlined v-model="(secondValue as any)" label="Minutes" class="q-mx-sm" />
        </div>
        <p>{{ totalMinutes }} minutes</p>
      </template>
      <template v-if="fieldType === 'FIELD_AGREEMENT_CHECKBOX'">
        <q-checkbox v-model="value" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_DISPLAY_TEXT'">
        <div class="tw-whitespace-pre-wrap">
          {{ fieldName }}
        </div>
      </template>
      <template v-if="fieldType === 'FIELD_DISPLAY_IMAGE'">
        <img />
      </template>
      <template v-if="fieldType === 'FIELD_IMAGE_UPLOAD_MULTIPLE'">
        <PhotoUploader
          :multiple="true"
          :recap-field-instance-id="getFieldId()"
          :recap-field-instance-repeating-index="props.repeatingIndex"
          @all-files-removed="allFilesRemoved"
          @file-added="fileAdded"
          ref="multiImageUploader"
        ></PhotoUploader>
      </template>
      <template v-if="fieldType === 'FIELD_FORMULA'">
        <q-input outlined disable readonly v-model="(value as any)" :label="fieldName" />
      </template>
      <template v-if="fieldType === 'FIELD_SELECT_WITH_NUMERIC_EQUIVALENT'">
        <div v-if="filteredOptions.length < 7" class="column">
          <q-radio
            v-for="option in filteredOptions"
            v-model="(value as any)"
            :val="option.numericEquivalent"
            :label="option.label"
            :key="`${recapType}-${taskOrStoreId}-${field.id}-${option.value}`"
          />
        </div>
        <q-select
          v-else
          v-model="value"
          option-label="label"
          option-value="numericEquivalent"
          :options="filteredOptions"
          emit-value
          map-options
        >
        </q-select>
      </template>
      <template v-if="fieldType === 'FIELD_RANK'">
        <Draggable v-model="filteredOptions" item-key="label">
          <template #item="{ element: option }">
            <div class="tw-bg-green-100 tw-rounded-md q-my-sm q-pa-sm tw-border tw-cursor-move">
              {{ option.label }}
            </div>
          </template>
        </Draggable>
      </template>
      <template v-if="fieldType === 'FIELD_DISPLAY_IMAGE' && field.field.image">
        <img class="tw-max-w-xl" :src="field.field.image" />
      </template>
      <small v-if="fieldNotes">{{ fieldNotes }}</small>
      <div v-if="fieldInfoText" @click="shouldDisplayInfoText = !shouldDisplayInfoText">
        <a role="button">Info</a>
        <div v-if="shouldDisplayInfoText" v-html="fieldInfoText"></div>
      </div>
      <div
        v-if="props.field.field.task_property === 'adsInstalled' && displayAdQuantity && adQuantity"
      >
        <em>Expecting {{ adQuantity }}</em>
      </div>
      <div v-if="quantityReasonCodeError">
        <em class="tw-text-red-600">{{ quantityReasonCodeError }}</em>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useCanvas } from '../../composables/recaps/useCanvas'
import { useDisplayLogic } from '../../composables/recaps/useDisplayLogic'
import { useLogic } from '../../composables/recaps/useLogic'
import { Field, FieldType, Formula, LocaleStrings, LogicGroup, Validation } from '../../types/recap'
import { ref, computed, watch, onMounted, Ref, inject, nextTick } from 'vue'
import Draggable from 'vuedraggable'
import PhotoUploader from '../forms/inputs/PhotoUploader.vue'
import { quantityReasonCodes } from '../../data/quantityReasonCodes'
import { storeToRefs } from 'pinia'
import { useTaskProfiles } from '../../store/useTaskProfiles'
import { useUserStore } from '../../store/useUserStore'
import { useRecapHelpers } from '../../composables/recaps/useRecapHelpers'
import { parseBool } from '../../utils'
import { useCodes } from '../../store/useCodes'
import { useSharedRecap, recapTaskData } from '../../composables/recaps/useRecap'
import { Nullish } from '../../types/utility'

// Props/Emits
const props = defineProps<{
  field: Field
  repeatingIndex?: number
}>()
const emit = defineEmits<{
  (e: 'value-change', data: ReturnType<typeof getValue>): void
}>()

const fieldLoaded = ref(false)

const {
  recapId,
  recapType,
  taskOrStoreId,
  recapLanguage,
  recapDefinition,
  triggerAllConditions,
  getInitialValue,
} = useSharedRecap()

// Task Data
const { getTaskByTaskId } = useTaskProfiles()
const task = computed(() => {
  if (recapType.value === 'task') {
    return getTaskByTaskId(taskOrStoreId.value)
  }
  return undefined
})
const singleImageUploader = ref<InstanceType<typeof PhotoUploader> | null>(null)
const multiImageUploader = ref<InstanceType<typeof PhotoUploader> | null>(null)
watch(taskOrStoreId, () => {
  ;(singleImageUploader.value as any)?.clearPhotos()
  ;(multiImageUploader.value as any)?.clearPhotos()
})
const adQuantity = computed(() =>
  task.value && task.value.ad_quantity ? task.value.ad_quantity : null
)
const quantityReasonCodeError = ref<string | null>(null)

// RAI Code
const raiCodeLocal = ref<string | Nullish>(null)

// Languages
const fieldName = computed(() => {
  return props.field.field.name_l10n[recapLanguage.value] || props.field.field.name
})
const fieldNotes = computed(() => {
  return props.field.field.notes_l10n[recapLanguage.value] || props.field.field.notes
})
const fieldInfoText = computed(() => {
  return (
    props.field.field.info_disclosure_text_l10n[recapLanguage.value] ||
    props.field.field.info_disclosure_text
  )
})
function getOptionText(option: { label: { [key in keyof LocaleStrings]: string }; value: any }) {
  return option.label[recapLanguage.value] || option.label['en_US']
}

// RAI Reason Code Specific Get Value
function getRaiReasonCodeValue(option: {
  label: { [key in keyof LocaleStrings]: string }
  value: any
}) {
  return option.value
}

// Display Logic
const { shouldBeHidden, handleAllDisplayLogic } = useDisplayLogic(
  recapId.value,
  taskOrStoreId,
  recapType
)
const isVisible = ref(true)
const errors = ref<string[]>([])
const fieldType = computed<FieldType>(() => props.field.field.defined_as)
const shouldDisplayName = computed(
  () =>
    fieldType.value !== 'FIELD_YES_NO' &&
    fieldType.value !== 'FIELD_SELECT' &&
    fieldType.value !== 'FIELD_SELECT_RADIO' &&
    fieldType.value !== 'FIELD_MULTISELECT' &&
    fieldType.value !== 'FIELD_SUBSECTION' &&
    fieldType.value !== 'FIELD_DISPLAY_TEXT' &&
    fieldType.value !== 'FIELD_RAI_CODE' &&
    fieldType.value !== 'FIELD_RAI_REASON_CODE' &&
    fieldType.value !== 'FIELD_IMAGE_REASON_CODE' &&
    fieldType.value !== 'FIELD_IMAGE_REASON_CODE_CUSTOM'
)
const shouldDisplayInfoText = ref(false)

handleDisplayLogicForImageReasonCode()
watch(raiCodeLocal, () => {
  handleDisplayLogicForImageReasonCode()
})

function handleDisplayLogicForImageReasonCode() {
  if (props.field.field.defined_as === 'FIELD_IMAGE_REASON_CODE') {
    if (!raiCodeLocal.value) {
      isVisible.value = false
      return
    }
    const { shouldDisplayImageQuestion } = useRecapHelpers()
    isVisible.value = shouldDisplayImageQuestion(task.value, raiCodeLocal.value)
  }
}

// Options
type FieldOption = {
  label: string
  value: string
  numericEquivalent?: number | null
  stringEquivalent?: string
  index?: number
}
const options = computed(() => {
  if (props.field.field.task_property === 'placementInstructionSequence') {
    const keys = ['placement_instruction1', 'placement_instruction2', 'placement_instruction3']
    const placementOptions = keys
      .map((key, index) => {
        const instruction = task.value ? task.value[key] : null
        if (instruction) {
          return {
            label: `${index + 1} - ${instruction}`,
            value: `${index + 1}`,
            numericEquivalent: index + 1,
            index,
          }
        }
        return null
      })
      .filter((opt) => !!opt) as FieldOption[]
    if (!placementOptions.length) {
      const isTestingRecap = inject('testingRecap', false)
      if (isTestingRecap) {
        placementOptions.push({
          label: 'Placeholder Instruction for Testing',
          value: '1',
          numericEquivalent: 1,
          index: 0,
        })
      }
    }
    return placementOptions
  } else if (props.field.field.task_property === 'quantityReasonCode') {
    return quantityReasonCodes
  } else if (props.field.field.options && !(props.field.field.options instanceof Array)) {
    const optionValues = Object.keys(props.field.field.options)
    let filteredSortedLabeledOptions = optionValues
      .filter((opt) => parseBool((props.field.field.options as any)[opt].active))
      .sort((opt1: any, opt2: any) => {
        try {
          const opt1Order = parseInt((props.field.field.options as any)[opt1].display_order)
          const opt2Order = parseInt((props.field.field.options as any)[opt2].display_order)
          if (opt1Order < opt2Order) {
            return -1
          }
          return 1
        } catch (e) {
          return -1
        }
      })
      .map((value: any, index: number) => ({
        label:
          ((props.field.field.options as any)[value] &&
            (props.field.field.options as any)[value].name_l10n &&
            recapLanguage.value &&
            (props.field.field.options as any)[value].name_l10n[recapLanguage.value]) ||
          (props.field.field.options as any)[value].name,
        value: value as string,
        numericEquivalent: (props.field.field.options as any)[value].numeric_equivalent,
        stringEquivalent: (props.field.field.options as any)[value].string_equivalent,
        index,
      }))

    if (props.field.field.defined_as === 'FIELD_RAI_CODE') {
      filteredSortedLabeledOptions = filteredSortedLabeledOptions.filter((opt) => {
        const raiCodes = (recapDefinition.value?.available_rai_codes ?? [])
          .filter((rai: any) => {
            if (useUserStore().loggedInWithSwitchTo) {
              return true
            }
            return parseBool(rai.visible_to_reps)
          })
          .map((rai: any) => String(rai.id))
        if (!raiCodes || !raiCodes.length) {
          // We don't have a list of available RAI codes
          // This can happen if the task is a merch task
          // Display the default options if they exist
          if (props.field.default_options && props.field.default_options.length) {
            return props.field.default_options
              ? props.field.default_options
                  .map((defopt) => defopt.option_value)
                  .includes(opt.stringEquivalent)
              : false
          }
          // If we don't have default options either, just display all RAI codes
          return true
        }
        // We have available RAI codes, so filter down to those
        return raiCodes.includes(String(opt.stringEquivalent))
      })
    }
    return filteredSortedLabeledOptions
  }
  return []
})
const filteredOptions = ref<FieldOption[]>(options.value as any)
watch(options, () => ((filteredOptions.value as any) = options.value))
watch(filteredOptions, () => {
  // If the field is a ranked order field,
  // make sure the value gets updated with the new order when it changes
  const anyIndexOutOfOrder = filteredOptions.value.filter((option, index) => option.index !== index)
  if (anyIndexOutOfOrder.length && props.field.field.defined_as === 'FIELD_RANK') {
    filteredOptions.value = filteredOptions.value.map((option, index) => ({
      ...option,
      index,
    }))
    value.value = filteredOptions.value.map((option) => option.value)
  }
})

// RAI Reason Code Values
const raiReasonCodes = ref<FieldOption[]>([])
watch(raiCodeLocal, () => {
  if (raiCodeLocal.value) {
    const { raiReasonCodeOptions } = storeToRefs(useCodes())
    raiReasonCodes.value =
      (raiReasonCodeOptions.value[raiCodeLocal.value as any] as unknown as
        | FieldOption[]
        | undefined) || []
  } else {
    raiReasonCodes.value = []
  }
})
const displayRaiReasonCodes = computed(() =>
  (fieldType as unknown as string) === 'FIELD_RAI_REASON_CODE'
    ? !!raiReasonCodes.value.length
    : true
)
if (props.field.field.defined_as === 'FIELD_RAI_REASON_CODE') {
  watch(raiReasonCodes, () => {
    if (!raiReasonCodes.value.length) {
      isVisible.value = false
    } else {
      isVisible.value = true
    }
  })
}

// Value Logic
const value = ref<any[] | boolean | string | number | null>(null)
const secondValue = ref<string | null>(null) // Only used for the Duration field
const totalMinutes = computed(() => {
  // Only used for the Duration field
  if (props.field.field.defined_as === 'FIELD_DURATION') {
    return +(value.value || 0) * 60 + +(secondValue.value || 0)
  }
  return null
})
watch(value, () => {
  const { isValidValue } = useLogic(null, [])
  if (isValidValue(value.value, secondValue.value, fieldType.value)) {
    handleAdQuantityLogic()
    emit('value-change', getValue())
    dispatchTaskPropertyEvent()
    errors.value.splice(0, errors.value.length)
  } else {
    value.value = null
  }
})
watch(secondValue, () => {
  const { isValidValue } = useLogic(null, [])
  if (isValidValue(value.value, secondValue.value, fieldType.value)) {
    emit('value-change', getValue())
    dispatchTaskPropertyEvent()
  } else {
    secondValue.value = null
  }
})
watch(isVisible, () => {
  // Rerun logic if this field is no longer visible.
  // This also ensures that hidden task property values are wiped.
  emit('value-change', getValue())
  dispatchTaskPropertyEvent()
})

// Ad Quantity Installed & Quantity Reason Code Logic
const adsInstalledLocal = ref<number | Nullish>(recapTaskData.value.ads_installed)
watch(
  recapTaskData,
  () => {
    // Handle any Neptune logic surrounding task data
    if (adsInstalledLocal.value !== recapTaskData.value.ads_installed) {
      adsInstalledLocal.value = recapTaskData.value.ads_installed
      if (props.field.field.task_property === 'quantityReasonCode') {
        value.value = null
      }
    }
    if (raiCodeLocal.value !== recapTaskData.value.rai_code) {
      raiCodeLocal.value = recapTaskData.value.rai_code
      if (props.field.field.defined_as === 'FIELD_RAI_REASON_CODE') {
        value.value = null
      }
    }
  },
  { deep: true }
)
const displayAdQuantity = computed(() => {
  if (props.field.field.task_property !== 'adsInstalled') {
    return true
  }
  let adQuantity = null
  try {
    adQuantity =
      props.field.field.task_property === 'adsInstalled'
        ? parseInt(task.value?.ad_quantity as string)
        : null
  } catch (e) {
    adQuantity = null
  }
  const codes: any[] = ((recapDefinition.value?.active_rai_codes ?? []) as any[]).filter((code) =>
    parseBool(code.success)
  )

  return adQuantity && adQuantity > 1 && props.field.field.task_property === 'adsInstalled'
    ? codes.map((code) => code.code).includes(raiCodeLocal.value as string)
    : false
})
watch(displayAdQuantity, () => {
  if (props.field.field.task_property === 'adsInstalled') {
    value.value = null
  }
})
const displayQuantityReasonCode = computed(() =>
  props.field.field.task_property === 'quantityReasonCode'
    ? !!(
        filteredOptions.value &&
        filteredOptions.value.length &&
        task.value &&
        task.value.ad_quantity &&
        adsInstalledLocal.value !== null &&
        adsInstalledLocal.value !== undefined &&
        adsInstalledLocal.value > -1 &&
        adsInstalledLocal.value < parseInt(task.value.ad_quantity as string)
      )
    : true
)
function handleAdQuantityLogic() {
  if (value.value && props.field.field.task_property === 'adsInstalled') {
    try {
      const parsedVal = parseInt(value.value as string)
      const parsedAdQty = parseInt(task.value?.ad_quantity as string)
      if (parsedAdQty) {
        if (parsedVal > parsedAdQty) {
          value.value = null
          adsInstalledLocal.value = null
          quantityReasonCodeError.value =
            'Bad Value: Cannot be greater than the expected ad quantity'
        } else if (parsedVal < 0) {
          value.value = null
          adsInstalledLocal.value = null
          quantityReasonCodeError.value = 'Bad Value: Cannot be less than 0'
        } else if (parsedVal === 0) {
          value.value = null
          adsInstalledLocal.value = null
          quantityReasonCodeError.value =
            'Bad Value: Please revisit RAI response if no ads were able to be installed'
        } else {
          quantityReasonCodeError.value = null
          value.value = parsedVal
          adsInstalledLocal.value = parsedVal
        }
      } else {
        value.value = null
        adsInstalledLocal.value = null
      }
    } catch (e) {
      value.value = null
      adsInstalledLocal.value = null
      quantityReasonCodeError.value = 'Bad Value: Must be a number'
    }
  }
}

// Specifically for image capture fields
const fileCount = ref<number>(0)
function allFilesRemoved() {
  value.value = null
  fileCount.value = 0
}
function fileAdded(data: { num: number }) {
  value.value = `${data.num} IMAGES UPLOADED`
  fileCount.value = data.num
}

// Canvas Logic
const signaturePad = ref<HTMLCanvasElement | null>(null)
const { setupCanvas, canvasValue, resetCanvas } = useCanvas(signaturePad)
watch(canvasValue, () => {
  value.value = canvasValue.value
})

// Exposed Functions
function getValue() {
  if (
    !isVisible.value ||
    shouldBeHidden.value ||
    !displayRaiReasonCodes.value ||
    !displayAdQuantity.value ||
    !displayQuantityReasonCode.value
  ) {
    // If the field isn't visible, act like it's null
    // so logic conditions will function properly
    return null
  }

  // Otherwise, get the value
  return getValueRaw()
}
function getValueRaw() {
  // Grabs the current value regardless of visibility
  if (secondValue.value) {
    // Only used for the Duration field
    let sum = 0
    if (value.value) {
      sum += +value.value * 60
    }
    return (sum + +secondValue.value).toString()
  } else if (props.field.field.defined_as === 'FIELD_RANK') {
    // We want the order of the filtered values
    return filteredOptions.value
  }
  return value.value
}
function getTaskProperty(): string | null {
  return props.field.field.task_property
}
function getDisplayRaiReasonCodes(): boolean {
  return displayRaiReasonCodes.value
}
function getDisplayAdQuantity(): boolean {
  return displayAdQuantity.value
}
function getDisplayQuantityReasonCode(): boolean {
  return displayQuantityReasonCode.value
}
function getFormula(): Formula | null {
  return props.field.field.formula
}
function getFormulaValue(): Ref<any[] | boolean | string | number | null> {
  return value
}
function getFieldId(): string {
  return props.field.id
}
function getEventsCustomId(): string {
  return props.field.events_custom_id
}
function getFieldType(): FieldType {
  return props.field.field.defined_as
}
function getFieldTypeId(): string {
  return props.field.field.type
}
function getIsVisible(): boolean {
  return isVisible.value
}
function getOptions() {
  return {
    options: options.value,
    filteredOptionsRef: filteredOptions,
    resetValue: () => (value.value = []),
  }
}
function getLogicGroups(): LogicGroup[] {
  return props.field.logic_groups
}
function getValidations(): Validation[] {
  return props.field.validations
}
// We're typing these functions as an `any` or `any[]` since this is actually a reference to another `RecapField.vue`
function runLogic(selfRef: any, dependenceRefs: any[]) {
  const { checkLogicConditions, runLogicModifiers } = useLogic(selfRef, dependenceRefs)
  isVisible.value = checkLogicConditions(props.repeatingIndex)
  runLogicModifiers(props.repeatingIndex)
}
function runValidation(selfRef: any, dependenceRefs: any[]): number {
  const { checkValidation } = useLogic(selfRef, dependenceRefs)
  errors.value = checkValidation(props.repeatingIndex)
  return errors.value.length
}
function getRepeatingIndex() {
  return props.repeatingIndex
}
defineExpose({
  shouldBeHidden,
  getValue,
  getValueRaw,
  getTaskProperty,
  getFormula,
  getFormulaValue,
  getFieldId,
  getEventsCustomId,
  getFieldType,
  getFieldTypeId,
  getIsVisible,
  getDisplayRaiReasonCodes,
  getDisplayAdQuantity,
  getDisplayQuantityReasonCode,
  getOptions,
  getLogicGroups,
  getValidations,
  runLogic,
  runValidation,
  getRepeatingIndex,
})

watch(triggerAllConditions, () => {
  if (triggerAllConditions.value) {
    triggerAllConditions.value = false
    emit('value-change', getValue())
    dispatchTaskPropertyEvent()
  }
})

function dispatchTaskPropertyEvent() {
  if (props.field.field.task_property) {
    const { getTaskDataKeyForTaskProperty } = useRecapHelpers()
    const propertyKey = getTaskDataKeyForTaskProperty(props.field.field.task_property)
    if (propertyKey) {
      let emitValue = null
      if (props.field.field.defined_as === 'FIELD_RAI_CODE') {
        const currOption = (options.value as FieldOption[]).find(
          (opt) => opt.value.toString() === value.value?.toString()
        )
        if (currOption) {
          const active_rai_codes = recapDefinition.value?.active_rai_codes
          if (!active_rai_codes || !active_rai_codes.length) {
            emitValue = null
          } else {
            emitValue =
              active_rai_codes.find(
                (rai_code: any) =>
                  rai_code.id?.toString() === currOption.stringEquivalent?.toString()
              )?.code ?? null
          }
          if (emitValue && emitValue === '0') {
            // Manually switch the completed RAI code to `000` if needed
            emitValue = '000'
          }
        }
      } else {
        emitValue = getValue()
      }
      if (
        emitValue &&
        ['ads_installed', 'bundles_added', 'pieces_remaining', 'quantity_installed'].includes(
          propertyKey
        )
      ) {
        // Cast the value to a number if necessary
        emitValue = parseInt(emitValue)
      }
      recapTaskData.value[propertyKey] = emitValue
    }
  }
}

async function runInitialLogic() {
  emit('value-change', getValue())
  if (recapType.value === 'task') {
    dispatchTaskPropertyEvent()
  }
  await nextTick()
  await nextTick()
}
onMounted(async () => {
  // Sets up the signature pad if there is one
  setupCanvas()

  // Get any existing values if there's an existing recap
  const { initialValue, initialSecondValue, imageCount } = await getInitialValue(
    props.field.id,
    props.field.field.defined_as,
    props.field.field.default_value
  )
  value.value = initialValue
  secondValue.value = initialSecondValue
  fileCount.value = imageCount

  // Run initial logic
  await runInitialLogic()

  // If the field is the RAI reason code, ensure the value is set
  // It would have been wiped if we had an existing response loaded
  if (props.field.field.defined_as === 'FIELD_RAI_REASON_CODE') {
    value.value = initialValue
    await runInitialLogic()
  }

  // Run Display Logic
  handleAllDisplayLogic(props.field)

  await nextTick()

  fieldLoaded.value = true
})
</script>
